import type { TFunction } from 'i18next'
import type { DealerOrderItem } from '../types'

// E.164 → читаемый вид для частых стран; остальные номера показываем как есть
export function formatPhone(phone: string | null | undefined): string {
  if (!phone) return ''
  const digits = phone.replace(/\D/g, '')
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`
  }
  if (digits.length === 11 && digits.startsWith('7')) {
    return `+7 ${digits.slice(1, 4)} ${digits.slice(4, 7)}-${digits.slice(7, 9)}-${digits.slice(9)}`
  }
  return phone
}

export function formatDateTime(iso: string | null | undefined, lang: string): string {
  if (!iso) return ''
  return new Date(iso).toLocaleString(lang, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
}

export function formatDate(iso: string | null | undefined, lang: string): string {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString(lang, { day: 'numeric', month: 'short', year: 'numeric' })
}

// «22 окт.» для текущего года, иначе с годом
export function formatShortDate(iso: string | null | undefined, lang: string): string {
  if (!iso) return ''
  const date = new Date(iso)
  const sameYear = date.getFullYear() === new Date().getFullYear()
  return date.toLocaleDateString(lang, sameYear ? { day: 'numeric', month: 'short' } : { day: 'numeric', month: 'short', year: 'numeric' })
}

export function formatTime(iso: string, lang: string): string {
  return new Date(iso).toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' })
}

// «5 минут назад», «вчера» — через Intl, без библиотек
export function formatRelative(iso: string | null | undefined, lang: string, now = Date.now()): string {
  if (!iso) return ''
  const diffSec = Math.round((new Date(iso).getTime() - now) / 1000)
  const rtf = new Intl.RelativeTimeFormat(lang, { numeric: 'auto' })
  const abs = Math.abs(diffSec)
  if (abs < 60) return rtf.format(0, 'second')
  if (abs < 3600) return rtf.format(Math.round(diffSec / 60), 'minute')
  if (abs < 86400) return rtf.format(Math.round(diffSec / 3600), 'hour')
  if (abs < 86400 * 30) return rtf.format(Math.round(diffSec / 86400), 'day')
  return formatDate(iso, lang)
}

// длительность в минутах → «2 ч 15 мин» / «3 д 4 ч»
export function formatDuration(minutes: number | null | undefined, t: TFunction): string {
  if (minutes === null || minutes === undefined || Number.isNaN(minutes)) return '—'
  const total = Math.max(0, Math.round(minutes))
  if (total < 1) return t('dealer.duration.less_than_minute')
  const days = Math.floor(total / 1440)
  const hours = Math.floor((total % 1440) / 60)
  const mins = total % 60
  if (days > 0) {
    return hours > 0 ? `${t('dealer.duration.days', { count: days })} ${t('dealer.duration.hours', { count: hours })}` : t('dealer.duration.days', { count: days })
  }
  if (hours > 0) {
    return mins > 0 ? `${t('dealer.duration.hours', { count: hours })} ${t('dealer.duration.minutes', { count: mins })}` : t('dealer.duration.hours', { count: hours })
  }
  return t('dealer.duration.minutes', { count: mins })
}

export function formatPercent(value: number | null | undefined, lang: string): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—'
  return new Intl.NumberFormat(lang, { style: 'percent', maximumFractionDigits: 0 }).format(value)
}

export function formatVehicle(year: number | null, model: string | null): string {
  return [year, model].filter(Boolean).join(' ')
}

// Даты для фильтров — локальный календарный день в формате YYYY-MM-DD
export function toIsoDay(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

// последние N дней включая сегодня
export function lastDaysRange(days: number): { from: string; to: string } {
  const to = new Date()
  const from = new Date()
  from.setDate(from.getDate() - (days - 1))
  return { from: toIsoDay(from), to: toIsoDay(to) }
}

// ссылка для предпросмотра: бэк не засчитывает такой просмотр как просмотр клиентом
export function previewUrl(reportUrl: string): string {
  try {
    const url = new URL(reportUrl)
    url.searchParams.set('preview', '1')
    return url.toString()
  } catch {
    return reportUrl + (reportUrl.includes('?') ? '&' : '?') + 'preview=1'
  }
}

// ссылка/телефон скопированы, но «Отправлено» не нажато — главный риск забыть или отправить дважды
export function isCopiedNotSent(item: DealerOrderItem): boolean {
  return item.deliveryStatus === 'NOT_SENT' && !!(item.linkCopiedAt || item.phoneCopiedAt)
}
